/* Welcome / Login — first-launch screen for the ORIO Android/iOS TWA wrapper.

   Design goal: when a user opens the app for the FIRST time coming from the
   Play Store or a promo (having NEVER seen the landing page on the web),
   they should be greeted by a warm, cinematic ORIO-style hero image
   followed by:
   • Bold headline + tagline
   • Login form (email + password) for returning users
   • Prominent "Iscriviti gratis" CTA that starts the multi-step
     registration flow already built at /register

   Kept the same route (/login) so ProtectedRoute redirects still work.
*/
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Lock, Eye, EyeOff, PawPrint, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { LOGO_WHITE } from "@/lib/assets";
import { orioIllustration } from "@/lib/illustrations";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Bentornato su ORIO");
      navigate(u.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const heroUrl = orioIllustration("welcome-hero");

  return (
    <div
      data-testid="welcome-screen"
      className="min-h-screen relative flex flex-col overflow-hidden"
      style={{ background: "#FBF7F1", color: "#1A1A1A" }}
    >
      {/* Hero image — top 45% of viewport on mobile, larger on desktop.
          Falls back to warm gradient if the image fails to load. */}
      <div
        className="relative w-full h-[46vh] sm:h-[52vh] shrink-0 overflow-hidden"
        style={{
          background: "linear-gradient(155deg, #FF7A1A 0%, #F4B83C 55%, #FFCB94 100%)",
        }}
      >
        <motion.img
          initial={{ scale: 1.06, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          src={heroUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        {/* Softer gradient overlay — bottom becomes cream to blend into the form */}
        <div className="absolute inset-0 pointer-events-none"
             style={{
               background: "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(251,247,241,0.35) 82%, #FBF7F1 100%)",
             }} />
        {/* ORIO logo — top-left, gradient-mask */}
        <div className="absolute top-6 left-6 z-10">
          <div
            aria-label="ORIO"
            className="h-8 w-[84px] drop-shadow-lg"
            style={{
              background: "linear-gradient(120deg, #ffffff 0%, #FFF6E6 100%)",
              WebkitMask: `url(${LOGO_WHITE}) no-repeat center / contain`,
              mask: `url(${LOGO_WHITE}) no-repeat center / contain`,
            }}
          />
        </div>
      </div>

      {/* Content — form + copy — bottom 55% of viewport */}
      <div className="flex-1 flex flex-col px-5 sm:px-6 -mt-16 sm:-mt-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35, ease: "easeOut" }}
          className="max-w-md mx-auto w-full"
        >
          {/* Eyebrow + Headline + Tagline */}
          <div className="mb-6">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur border border-[#F0EBE3] text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C25612] mb-3">
              <Sparkles className="h-3 w-3" />
              Benvenuto su ORIO
            </div>
            <h1 className="font-display text-[34px] sm:text-[40px] leading-[1.02] tracking-tight text-[#0F0F0F]">
              Il tuo cane,<br />
              <span
                className="inline-block bg-clip-text text-transparent"
                style={{
                  backgroundImage: "linear-gradient(120deg, #FF7A1A 0%, #F4B83C 50%, #2DBA60 100%)",
                }}
              >
                protetto e amato.
              </span>
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-[#5A5A5A]">
              SOS smarrimento, mappa live, passaporto digitale e la community italiana più attenta al benessere dei cani.
            </p>
          </div>

          {/* Login form */}
          <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
            <label className="block">
              <span className="sr-only">Email</span>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A9A9A]" />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  data-testid="login-email"
                  className="w-full pl-11 pr-4 h-12 rounded-2xl bg-white border border-[#F0EBE3] text-[15px] outline-none focus:border-[#FF7A1A] focus:ring-2 focus:ring-[#FF7A1A]/15 transition"
                />
              </div>
            </label>
            <label className="block">
              <span className="sr-only">Password</span>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A9A9A]" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  data-testid="login-password"
                  className="w-full pl-11 pr-11 h-12 rounded-2xl bg-white border border-[#F0EBE3] text-[15px] outline-none focus:border-[#FF7A1A] focus:ring-2 focus:ring-[#FF7A1A]/15 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Nascondi password" : "Mostra password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center text-[#9A9A9A] active:scale-95 transition"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-12 rounded-2xl font-bold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-70 active:scale-[0.98] transition"
              style={{
                background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                color: "white",
                boxShadow: "0 12px 28px -8px rgba(255,122,26,0.45)",
              }}
            >
              {loading ? "Accesso in corso…" : (
                <>Entra <ArrowRight className="h-4 w-4" strokeWidth={2.5} /></>
              )}
            </button>
            <div className="text-center pt-1">
              <Link
                to="/forgot"
                data-testid="login-forgot"
                className="text-[13px] text-[#7A7A7A] hover:text-[#1A1A1A] underline decoration-dotted underline-offset-2"
              >
                Password dimenticata?
              </Link>
            </div>
          </form>

          {/* Google Sign-In — Emergent managed OAuth.
              REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
              REDIRECT URLS, THIS BREAKS THE AUTH */}
          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-[#F0EBE3]" />
            <span className="text-[11px] uppercase tracking-widest text-[#9A9A9A]">oppure</span>
            <div className="h-px flex-1 bg-[#F0EBE3]" />
          </div>
          <button
            type="button"
            data-testid="login-google-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + "/app";
              window.location.href =
                "https://auth.emergentagent.com/?redirect=" +
                encodeURIComponent(redirectUrl);
            }}
            className="w-full h-12 rounded-2xl bg-white border border-[#E5DECF] flex items-center justify-center gap-3 active:scale-[0.98] transition-transform"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.15.8 3.87 1.5l2.64-2.55C16.9 3.35 14.65 2.4 12 2.4 6.75 2.4 2.5 6.65 2.5 12s4.25 9.6 9.5 9.6c5.5 0 9.15-3.85 9.15-9.3 0-.62-.07-1.1-.15-1.6H12z"/>
              <path fill="#34A853" d="M3.88 7.4l3.2 2.35C7.98 8.05 9.85 6.9 12 6.9c1.9 0 3.15.8 3.87 1.5l2.64-2.55C16.9 4.35 14.65 3.4 12 3.4 8.15 3.4 4.9 5.7 3.88 7.4z" opacity="0"/>
              <path fill="#FBBC05" d="M12 21.6c2.55 0 4.7-.85 6.27-2.3l-3-2.35c-.85.6-2 .95-3.27.95-2.55 0-4.7-1.7-5.47-4.05l-3.1 2.4C4.8 19.35 8.15 21.6 12 21.6z"/>
              <path fill="#4285F4" d="M21.15 12c0-.62-.07-1.1-.15-1.6H12v3.9h5.5c-.24 1.05-.95 2.05-2 2.75l3 2.35c1.75-1.6 2.65-4 2.65-7.4z"/>
            </svg>
            <span className="text-[14px] font-semibold text-[#1A1A1A]">
              Continua con Google
            </span>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-[#F0EBE3]" />
            <span className="text-[11px] uppercase tracking-widest text-[#9A9A9A]">Nuovo su ORIO?</span>
            <div className="h-px flex-1 bg-[#F0EBE3]" />
          </div>

          {/* Signup CTA — prominent, warm, animated */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <Link
              to="/register"
              data-testid="welcome-signup"
              className="w-full h-14 rounded-2xl bg-white border-2 border-[#1A1A1A] flex items-center justify-between gap-3 px-4 group active:scale-[0.98] transition-transform"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                  }}
                >
                  <PawPrint className="h-5 w-5 text-white" strokeWidth={2.4} />
                </div>
                <div className="min-w-0 text-left">
                  <div className="font-display text-[15px] text-[#0F0F0F] leading-tight">
                    Iscriviti gratis
                  </div>
                  <div className="text-[11.5px] text-[#7A7A7A] leading-tight">
                    30 giorni Pro inclusi · 90 secondi · gratis
                  </div>
                </div>
              </div>
              <ArrowRight
                className="h-5 w-5 text-[#1A1A1A] shrink-0 transition-transform group-hover:translate-x-1"
                strokeWidth={2.4}
              />
            </Link>
          </motion.div>

          {/* Footer: legal + landing link (only visible on desktop) */}
          <div className="mt-6 mb-6 text-center">
            <div className="text-[11px] text-[#9A9A9A] leading-relaxed">
              Continuando accetti i{" "}
              <a href="/terms" className="underline decoration-dotted">Termini</a>
              {" "}e la{" "}
              <a href="/privacy" className="underline decoration-dotted">Privacy</a>.
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
